import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, Button } from '@mui/material';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import chartOfAccountsApi from '../../../api/chartOfAccountsApi';

export default function ImportJournalEntryDialog({ open, onClose, onImport }) {
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [data, setData] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  
  const [mapping, setMapping] = useState({
    accountCode: '',
    debit: '',
    credit: '',
    description: ''
  });

  useEffect(() => {
    if (open) {
      setLoadingAccounts(true);
      chartOfAccountsApi.getPostable()
        .then(res => setAccounts(res.data || []))
        .catch(err => console.error("Error loading accounts", err))
        .finally(() => setLoadingAccounts(false));
    }
  }, [open]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: 'binary' });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const jsonData = XLSX.utils.sheet_to_json(ws, { header: 1 });
      
      if (jsonData.length > 0) {
        setHeaders(jsonData[0]); 
        setData(jsonData.slice(1)); 
        
        // Auto-guess mapping
        const guess = { accountCode: '', debit: '', credit: '', description: '' };
        jsonData[0].forEach(h => {
          const lower = String(h).toLowerCase();
          if (lower.includes('code') || lower.includes('account') || lower.includes('رمز') || lower.includes('حساب')) guess.accountCode = h;
          if (lower.includes('debit') || lower.includes('debt') || lower.includes('مدين')) guess.debit = h;
          if (lower.includes('credit') || lower.includes('cred') || lower.includes('دائن')) guess.credit = h;
          if (lower.includes('desc') || lower.includes('note') || lower.includes('وصف') || lower.includes('بيان') || lower.includes('ملاحظات')) guess.description = h;
        });
        setMapping(guess);
      }
    };
    reader.readAsBinaryString(selectedFile);
  };

  const handleImport = () => {
    if (!mapping.accountCode) {
      toast.warning('يرجى تحديد عمود رمز الحساب');
      return;
    }
    if (!mapping.debit && !mapping.credit) {
      toast.warning('يرجى تحديد عمود المدين أو الدائن على الأقل');
      return;
    }

    const codeIndex = headers.indexOf(mapping.accountCode);
    const debitIndex = mapping.debit ? headers.indexOf(mapping.debit) : -1;
    const creditIndex = mapping.credit ? headers.indexOf(mapping.credit) : -1;
    const descIndex = mapping.description ? headers.indexOf(mapping.description) : -1;

    const importedLines = [];
    let notFoundCount = 0;

    data.forEach(row => {
      const rawCode = row[codeIndex];
      if (!rawCode) return;

      const codeStr = String(rawCode).trim();
      
      const account = accounts.find(a => String(a.code) === codeStr);
      
      if (account) {
        const debit = debitIndex > -1 ? (parseFloat(row[debitIndex]) || 0) : 0;
        const credit = creditIndex > -1 ? (parseFloat(row[creditIndex]) || 0) : 0;
        const description = descIndex > -1 ? (row[descIndex] || '') : '';

        // Skip empty lines
        if (debit === 0 && credit === 0) return;

        importedLines.push({
          accountId: account.id,
          accountCode: account.code,
          accountName: account.nameAr,
          description,
          debit,
          credit
        });
      } else {
        notFoundCount++;
      }
    });

    if (importedLines.length === 0) {
      toast.error('لم يتم العثور على أي حسابات مطابقة');
      return;
    }

    onImport(importedLines);
    if (notFoundCount > 0) {
      toast.warning(`تم استيراد ${importedLines.length} قيد. لم يتم العثور على حساب لـ ${notFoundCount} صف.`);
    } else {
      toast.success(`تم استيراد ${importedLines.length} قيد بنجاح`);
    }
    onClose();
  };

  const handleDownloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ["Account Code", "Debit", "Credit", "Description"],
      ["1101", "1000", "0", "Opening Cash"],
      ["2101", "0", "1000", "Opening Capital"]
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "JournalTemplate");
    XLSX.writeFile(wb, "journal_entry_template.xlsx");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth dir="rtl">
      <DialogTitle className="font-bold text-lg flex justify-between items-center">
        <span>استيراد قيود من Excel</span>
        <Button variant="text" size="small" onClick={handleDownloadTemplate} className="text-blue-600 text-sm">
          📥 تحميل قالب تجريبي
        </Button>
      </DialogTitle>
      <DialogContent className="space-y-4 pt-4">
        
        {/* File Input */}
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-gray-50">
          <input 
            type="file" 
            accept=".xlsx, .xls, .csv" 
            onChange={handleFileChange} 
            className="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-full file:border-0
              file:text-sm file:font-semibold
              file:bg-blue-50 file:text-blue-700
              hover:file:bg-blue-100"
          />
        </div>

        {/* Mapping Section */}
        {headers.length > 0 && (
          <div className="grid grid-cols-1 gap-4 bg-white p-4 rounded border">
            <h4 className="font-bold border-b pb-2">تعيين الأعمدة</h4>
            
            <div>
              <label className="block text-sm font-bold mb-1">رمز الحساب <span className="text-red-500">*</span></label>
              <select 
                value={mapping.accountCode} 
                onChange={(e) => setMapping({...mapping, accountCode: e.target.value})}
                className="w-full border rounded p-2 bg-white"
              >
                <option value="">-- اختر العمود --</option>
                {headers.map((h, i) => <option key={i} value={h}>{h}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-bold mb-1">مدين</label>
                  <select 
                    value={mapping.debit} 
                    onChange={(e) => setMapping({...mapping, debit: e.target.value})}
                    className="w-full border rounded p-2 bg-white"
                  >
                    <option value="">-- اختر العمود --</option>
                    {headers.map((h, i) => <option key={i} value={h}>{h}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1">دائن</label>
                  <select 
                    value={mapping.credit} 
                    onChange={(e) => setMapping({...mapping, credit: e.target.value})}
                    className="w-full border rounded p-2 bg-white"
                  >
                    <option value="">-- اختر العمود --</option>
                    {headers.map((h, i) => <option key={i} value={h}>{h}</option>)}
                  </select>
                </div>
            </div>

            <div>
              <label className="block text-sm font-bold mb-1">البيان / الوصف</label>
              <select 
                value={mapping.description} 
                onChange={(e) => setMapping({...mapping, description: e.target.value})}
                className="w-full border rounded p-2 bg-white"
              >
                <option value="">-- اختر العمود --</option>
                {headers.map((h, i) => <option key={i} value={h}>{h}</option>)}
              </select>
            </div>
            
            <div className="text-sm text-gray-500 mt-2">
              عدد الصفوف المكتشفة: {data.length}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outlined" onClick={onClose} className="border-gray-300 text-gray-700">إلغاء</Button>
          <Button 
            variant="contained" 
            onClick={handleImport} 
            disabled={!file || !mapping.accountCode || (!mapping.debit && !mapping.credit) || loadingAccounts} 
            className="bg-blue-600 text-white hover:bg-blue-700"
          >
            {loadingAccounts ? 'جاري تحميل الحسابات...' : 'استيراد'}
          </Button>
        </div>

      </DialogContent>
    </Dialog>
  );
}
